import React from 'react';

/**
 * Error Boundary component to catch and handle React component errors
 * Prevents entire application from crashing when individual components fail
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('Application Error Caught by ErrorBoundary:', error, errorInfo);

    // Store error details for display and debugging
    this.setState({
      error,
      errorInfo
    });

    // You could also log errors to a service here
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // Reset the error state to try rendering again
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleRefresh = () => {
    // Full page refresh as a last resort
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: 'var(--danger-soft)',
          border: '1px solid rgba(220, 38, 38, 0.2)',
          borderRadius: '8px',
          margin: '20px',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <h2 style={{ color: 'var(--danger)', marginBottom: '16px' }}>
            Something went wrong
          </h2>

          <p style={{ color: 'var(--danger-hover)', marginBottom: '24px' }}>
            The application encountered an error and couldn't continue.
            You can try reloading the component or refreshing the entire page.
          </p>

          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={this.handleReset}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                marginRight: '12px'
              }}
            >
              Try Again
            </button>

            <button
              onClick={this.handleRefresh}
              style={{
                backgroundColor: 'var(--danger)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
          </div>

          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{
              textAlign: 'left',
              backgroundColor: 'var(--danger-soft-hover)',
              padding: '16px',
              borderRadius: '6px',
              marginTop: '20px'
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Error Details (Development Only)
              </summary>
              <pre style={{
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '200px',
                marginTop: '8px',
                whiteSpace: 'pre-wrap'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}

          <p style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            marginTop: '20px'
          }}>
            If this problem continues, please contact support or check the browser console for more details.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;